import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { Supplier } from '@nucleus-commerce/api-client/platform';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { Mono } from '@/shared/ui/mono';

import { useDeleteSupplier } from '../hooks/use-delete-supplier';
import { useSuppliers } from '../hooks/use-suppliers';

import { SupplierDialog } from './supplier-dialog';

/** Suppliers of the shared pool. Deleting one keeps its stones, without a supplier. */
export function SuppliersPanel({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation(['diamonds', 'common']);
  const query = useSuppliers();
  const remove = useDeleteSupplier();
  const [editing, setEditing] = useState<{ supplier: Supplier | null } | null>(null);
  const [deleting, setDeleting] = useState<Supplier | null>(null);
  const suppliers = query.data?.items ?? [];

  return (
    <Card className="gap-0 overflow-hidden py-0" data-tour="platform.diamonds.suppliers">
      <CardHeader className="border-b px-5 py-4">
        <CardTitle>{t('diamonds:suppliers.title')}</CardTitle>
        <CardDescription>{t('diamonds:suppliers.description')}</CardDescription>
        {canManage ? (
          <CardAction>
            <Button size="sm" onClick={() => setEditing({ supplier: null })}>
              <Plus aria-hidden />
              {t('diamonds:suppliers.add')}
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      {query.isError ? (
        <div className="p-5">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : (
        <Table className="text-[13px]" aria-label={t('diamonds:suppliers.title')}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-10 pl-5 text-xs font-medium">
                {t('diamonds:suppliers.columns.name')}
              </TableHead>
              <TableHead className="h-10 text-xs font-medium">
                {t('diamonds:suppliers.columns.currency')}
              </TableHead>
              <TableHead className="h-10 text-xs font-medium">
                {t('diamonds:suppliers.columns.leadTime')}
              </TableHead>
              <TableHead className="h-10 text-xs font-medium">
                {t('diamonds:suppliers.columns.notes')}
              </TableHead>
              <TableHead className="h-10 pr-5">
                <span className="sr-only">{t('diamonds:suppliers.columns.actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isSuccess && suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground h-20 text-center">
                  {t('diamonds:suppliers.empty')}
                </TableCell>
              </TableRow>
            ) : (
              suppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell className="py-2.5 pl-5 font-medium">{supplier.name}</TableCell>
                  <TableCell className="py-2.5">
                    <Mono className="text-foreground">{supplier.currency}</Mono>
                  </TableCell>
                  <TableCell className="py-2.5">
                    {supplier.leadTimeDays === null
                      ? '—'
                      : t('diamonds:suppliers.days', { count: supplier.leadTimeDays })}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-80 truncate py-2.5">
                    {supplier.notes ?? ''}
                  </TableCell>
                  <TableCell className="py-2.5 pr-5 text-right whitespace-nowrap">
                    {canManage ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('diamonds:suppliers.edit', { name: supplier.name })}
                          onClick={() => setEditing({ supplier })}
                        >
                          <Pencil aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={t('diamonds:suppliers.delete', { name: supplier.name })}
                          onClick={() => setDeleting(supplier)}
                        >
                          <Trash2 aria-hidden />
                        </Button>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      <SupplierDialog
        open={editing !== null}
        supplier={editing?.supplier ?? null}
        onClose={() => setEditing(null)}
      />
      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('diamonds:suppliers.deleteTitle', { name: deleting?.name ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('diamonds:suppliers.deleteBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {t('common:actions.cancel')}
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                if (!deleting) return;
                remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
              }}
            >
              {t('diamonds:suppliers.deleteConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
